import React from 'react';
import { Route } from 'react-router-dom';
import provider from './authProvider';
import config from '../config';
import { createBrowserHistory } from 'history';
import fetch from './fetch';
import AccessDenied from './AccessDenied';

const HTTP_UNAUTHORIZED = 401;
const HTTP_400_CODES = '4';
const INTERNAL_SERVER_ERROR = 500;

const history = createBrowserHistory();
export class Authentication extends React.Component<any, any> {
  constructor(props) {
    super(props);
    this.state = { isAuthenticated: false, isInitialized: false };
  }

  async componentDidMount() {
    provider.init().then(async (initState) => {
      if (initState.path) {
        history.push(initState.path);
      }
      const { isAuthenticated } = provider.getState();
      if (isAuthenticated === true) {
        try {
          const userInfo = await getUserInfo();

          if (provider.setState) {
            provider.setState({
              isMappingAdmin: (userInfo as any).isMappingAdmin,
              userGroups: (userInfo as any).groupsAll,
            });
          } else {
            // Fallback if provider is a simple object
            provider.state = {
              ...provider.getState(),
              isMappingAdmin: (userInfo as any).isMappingAdmin,
              userGroups: (userInfo as any).groupsAll,
            };
          }
        } catch (err) {
          console.error('Failed to verify admin status', err);
        }
      }
      this.setState({ ...provider.getState() });
    });
  }

  render() {
    if (!this.state.isInitialized) {
      return 'Loading....';
    }
    return this.props.children;
  }
}

export class SecureRoute extends Route {
  props: any;
  render() {
    const authState = getAuthState();
    if (!authState.isAuthenticated) {
      if (this.props.notSecure) {
        return this.props.notSecure();
      } else {
        login(this.props.location?.pathname);
        return null;
      }
    }
    return super.render();
  }
}

export function withAuthentication(WrappedComponent) {
  return class extends React.Component {
    render() {
      return (
        <WrappedComponent
          authState={getAuthState()}
          authService={getAuthService()}
          {...this.props}
        />
      );
    }
  };
}

/**
 * HOC that renders the wrapped component only if the user has mapping admin access.
 * Otherwise renders AccessDenied. Use this for pages that require mapping admin.
 */
export function withRequireMappingAdmin(WrappedComponent) {
  return class extends React.Component {
    render() {
      const authState = getAuthState();
      if (!authState?.isMappingAdmin) {
        return <AccessDenied />;
      }
      return (
        <WrappedComponent
          authState={authState}
          authService={getAuthService()}
          {...this.props}
        />
      );
    }
  };
}

export function getUserInfo() {
  return new Promise((resolve, reject) => {
    authFetch(config.endpoints.verifyUserToken, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((result) => resolve(result))
      .catch((error) => reject(error));
  });
}

export function getThoughtSpotToken(token) {
  return new Promise((resolve, reject) => {
    authFetch(config.endpoints.getThoughtSpotToken, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((result) => resolve(result))
      .catch((error) => reject(error));
  });
}

// A dashboard token for one Jitto dashboard, as the signed-in user (ANS-545, ANS-678).
// Resolves to @jitto/analyze-embed's { token, dashboardId, embedUrl, expiresIn }; the backend
// forwards the user's ID token to Jitto, which decides which dashboards may be embedded.
//
// A user Jitto has never seen gets 409 with a link_url: the browser goes there once,
// through Jitto's sign-in (Okta answers from the session it already has), and comes
// back to this page, which asks again. A flag in sessionStorage stops a loop.
export const JITTO_LINK_FLAG = 'jitto-embed-link-tried';

export function getJittoEmbedToken(dashboardId) {
  return authFetch(config.endpoints.getJittoEmbedToken, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Okta-Id-Token': provider.getState().idToken || '',
    },
    body: JSON.stringify({ dashboardId }),
  })
    .then((response) => {
      sessionStorage.removeItem(JITTO_LINK_FLAG);
      return response.json();
    })
    .catch(async (response) => {
      if (response && response.status === 409 && !sessionStorage.getItem(JITTO_LINK_FLAG)) {
        const body = await response.json().catch(() => ({}));
        if (body.code === 'not_linked' && body.link_url) {
          sessionStorage.setItem(JITTO_LINK_FLAG, '1');
          const back = encodeURIComponent(window.location.href);
          window.location.assign(`${body.link_url}?return=${back}`);
          return new Promise(() => {}); // the page is leaving
        }
      }
      throw response;
    });
}

export function authFetch(uri, config?: any): Promise<any> {
  return new Promise((resolve, reject) => {
    const _config = { ...config };
    _config.headers = _config.headers || {};
    _config.headers['Authorization'] = `Bearer ${
      provider.getState().accessToken
    }`;

    fetch(uri, _config)
      .then((response) => {
        if (response.status === HTTP_UNAUTHORIZED) {
          provider.logout();
          return;
        }
        if (
          response.status &&
          (response.status.toString().startsWith(HTTP_400_CODES) ||
            response.status >= INTERNAL_SERVER_ERROR)
        ) {
          return reject(response);
        }
        return resolve(response);
      })
      .catch((error) => {
        console.log(error); //todo: wrap exception into response object
        return reject(error);
      });
  });
}

function thoughtspotLogout() {
  fetch(`${config.thoughtSpotHost}/callosum/v1/tspublic/v1/session/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'X-Requested-By': 'ThoughtSpot',
    },
  });
}

export function getAuthState() {
  return { ...provider.getState() };
}

function getAuthService() {
  return { logout, login };
}

function login(resource) {
  return provider.login(resource);
}

function logout() {
  // With Jitto Answer on, there is no ThoughtSpot session to end.
  if (!config.jittoEmbedActiveFlag) thoughtspotLogout();
  return provider.logout();
}
